import { FabricChannel } from '@atlaskit/analytics-listeners/types';

export const ANALYTICS_CHANNEL: FabricChannel.aiMate = FabricChannel.aiMate;

const REMIX_SOURCES = [
	'remix',
	'inlineRovoFromRemix',
	'remixModalFromToolbar',
	'remixModalFromBlockControls',
	'remixModalFromBlockMenu',
	'remixModalFromFloatingToolbar',
	'remixModalFromViewPageFloatingToolbar',
	'remixModalFromViewPageNudge',
	'remixModalFromNudge',
	'remixModalFromObjectLevelSidebar',
	'remixModalFromPasteMenu',
	'remixStagingArea',
	'remixModalFromMediaInserts',
] as const;

type RemixInteractionSource = (typeof REMIX_SOURCES)[number];
type ScheduledChatInteractionSource = 'scheduledChatRenderer';

type GoogleDocCreateInteractionSource = 'googleDocCreateRenderer';

type JiraEpicsSuggestInteractionSource = 'jiraEpicsSuggestRenderer';
type StudioUpdateAgentDraftInteractionSource = 'studioUpdateAgentDraftRenderer';

export const SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE = 'smartLinkRovoButton';

export type InteractionSource =
	| RemixInteractionSource
	| GoogleDocCreateInteractionSource
	| ScheduledChatInteractionSource
	| JiraEpicsSuggestInteractionSource
	| StudioUpdateAgentDraftInteractionSource
	| typeof SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE
	| 'agenticPlanToolRenderer'
	| 'askQuestionRenderer'
	| 'chatInput'
	| 'confluenceAifcAdfUpdateRenderer'
	| 'confluenceApprovalRenderer'
	| 'confluenceFolderCreateRenderer'
	| 'confluencePageAddLabelRenderer'
	| 'confluencePageCommentRenderer'
	| 'confluencePageCreateRenderer'
	| 'confluencePageMoveRenderer'
	| 'confluencePageRenameRenderer'
	| 'deepResearchConfluencePageCreateRenderer'
	| 'editMessage'
	| 'editor'
	| 'embeddedAgentConversationStarters'
	| 'emptyStateSuggestions'
	| 'executePythonRenderer'
	| 'focusAskCreateAction'
	| 'followUps'
	| 'inlineRovo'
	| 'jiraAddViewCreateWithRovo'
	| 'jiraCreateSprintAction'
	| 'jiraIssueAssignRenderer'
	| 'jiraIssueDeleteRenderer'
	| 'jiraIssueMoveRenderer'
	| 'jiraIssueTransitionRenderer'
	| 'jiraListChildCreateRovo'
	| 'jiraMoveToSprintAction'
	| 'jiraPlansAddDependencyRenderer'
	| 'jiraPlansAddProgramBoardRenderer'
	| 'jiraPlansCreateReleaseRenderer'
	| 'jiraPlansCreateWorkItemRenderer'
	| 'jiraPlansDeleteProgramBoardRenderer'
	| 'jiraPlansManageFeaturesRenderer'
	| 'jiraPlansRemoveDependencyRenderer'
	| 'jiraPlansUpdateWorkSourceRenderer'
	| 'jiraStartSprintAction'
	| 'jiraTimelineChildCreateRovo'
	| 'jiraVersionCreateRenderer'
	| 'jiraWorkflowEditorRenderer'
	| 'jsmCreateRequestActionUnifiedRenderer'
	| 'jsmCreateSpaceAction'
	| 'jsmJourneyBuilderActionRenderer'
	| 'jsmLivechatActionRenderer'
	| 'jsmSelectHardwareTypesAction'
	| 'movedOver'
	| 'multipleChoiceQuestionsToolRenderer'
	| 'ntpSuggestion'
	| 'planCompleteRenderer'
	| 'profileCardConversationStarters'
	| 'resendMessage'
	| 'repositoryChoiceToolRenderer'
	| 'resumptionRenderer'
	| 'rightClickDefine'
	| 'rightClickSummarize'
	| 'slackSendChannelMessageRenderer'
	| 'smartCreate'
	| 'studioLandingPageRedirectRenderer'
	| 'studioUpdateAgentDraftAction'
	| 'updateAgentRenderer'
	| 'urlParam'
	| 'viewAgentConversationStarters'
	| 'rovoPublishAgentRenderer';

/**
 * Which UI surface triggered a resend. `interactionSource: 'resendMessage'` tells us a
 * resend happened but not where from, so every `useResendMessage` caller declares itself
 * here and the value is emitted as the `resendSource` analytics attribute.
 *
 * `'unknown'` is the default for callers that do not declare a source. A non-trivial
 * `'unknown'` volume means a resend path exists that we have not identified yet.
 *
 * **IMPORTANT — enumerated constants only.** Never derive this from user input.
 */
export type ResendSource =
	| 'chatErrorCard'
	| 'connectorRecommendation'
	| 'editMessage'
	| 'stagingAreaErrorScreen'
	| 'writingStylesCard'
	| 'unknown';

// Widened to Set<InteractionSource> so .has() accepts any InteractionSource directly
export const REMIX_INTERACTION_SOURCES: Set<InteractionSource> = new Set(REMIX_SOURCES);

// Which context a follow up was triggered from
export type FollowUpSource =
	| 'ai-mate'
	| 'extension'
	| 'none'
	| 'page-summary'
	| 'reading-aids'
	| 'sain'
	| 'ai-smart-button-dynamic-surface'
	| string;

/**
 * Closed vocabulary of always-enabled launch sources permitted to be emitted as the
 * `launchSource` analytics attribute on `aiResult actioned` / `aiResult adopted`.
 * `GATED_LAUNCH_SOURCE_ALLOWLIST` contains rollout-gated sources. `FollowUpSource`
 * intentionally keeps a `| string` fallback for callers, so the launch-source
 * allowlists provide the controlled vocabulary + runtime validation required for a
 * GASv3 analytics field. Add a new named literal to the appropriate allowlist (and get
 * analytics review) before emitting it.
 */
export const LAUNCH_SOURCE_ALLOWLIST: Set<string> = new Set<string>([
	'automation-discovery-meeting-notes-nudge',
	'automation-discovery-content-reviewer-nudge',
	'jiraListChildCreateRovo',
	'jiraTimelineChildCreateRovo',
	'jsmSurveyAggregatedInsights',
	'recommendation-card-nudge',
]);

/**
 * Launch sources admitted only when their associated rollout is enabled.
 */
export const GATED_LAUNCH_SOURCE_ALLOWLIST: Set<string> = new Set<string>([
	'jiraAddViewCreateWithRovo',
]);

/**
 * Runtime allowlist check used at the pubsub boundary so a free-form
 * `launchSource` string can never reach analytics payloads.
 */
export const isAllowedLaunchSource = (
	value: string | undefined,
	includeGatedLaunchSources = false,
): value is string =>
	value !== undefined &&
	(LAUNCH_SOURCE_ALLOWLIST.has(value) ||
		(includeGatedLaunchSources && GATED_LAUNCH_SOURCE_ALLOWLIST.has(value)));

export enum AUTOSUGGEST_SOURCE {
	ROVO_CHAT = 'rovo-chat',
	CONFLUENCE_AIFC = 'confluence-aifc',
	CONFLUENCE_AIFC_SLIDES = 'confluence-aifc-slides',
	CONFLUENCE_AIFC_WHITEBOARD = 'confluence-aifc-whiteboard',
	CONFLUENCE_AIFC_DATABASE = 'confluence-aifc-database',
	CONFLUENCE_AIFC_LIVE_DOC = 'confluence-aifc-live-doc',
}

/**
 * `actionSubject` for the in-thread third-party connect prompt (CCINT-8714): `resolved` when the
 * offer is computed (including when it is suppressed), `viewed` when it renders.
 *
 * Shared from here because the two events are emitted from different packages —
 * `conversation-assistant` computes the offer, `conversation-assistant-chat-prompt-input` renders
 * it — and only the first of those may import the second.
 */
export const THIRD_PARTY_CONNECT_PROMPT_SUBJECT = 'thirdPartyConnectPrompt';
